/**
 * Bundle loading and the decoded-texture budget (ARCHITECTURE.md §8.5, SKEL-STORY §3.5).
 *
 * Three things live here:
 *
 * 1. **URL resolution.** Every `assets[].url` is resolved with
 *    `new URL(asset.url, baseUrl)` — never string concatenation (§4.6.4).
 *    That is what lets the same bundle be served from
 *    `/api/stories/{uid}/bundle/` (puppet parts at `tex/<sha>.png`) and from
 *    the root of an exported zip, and it also copes with absolute
 *    `/api/media/…` plate urls.
 * 2. **Byte-weighted preload.** Scene 0 must be fully loaded before
 *    "Tap to start": its plate plus every part texture of every puppet (all
 *    views). The progress bar is weighted by `assets[].bytes` (a plate the API
 *    derives says `bytes: 0`, so an empty size falls back to its kind's weight).
 *    Requests run **in parallel** (`LOAD_CONCURRENCY`): one after another, a
 *    story's 20-70 parts cost one round trip each, ~10 s on a phone network.
 * 3. **Packed parts.** A bundle baked since `backend/app/story/pack.py` packs
 *    the parts into one or two WebP pages (`kind: "pack"`) and marks each part
 *    `pack: {id, x, y}`. The loader fetches the page instead of the parts, and
 *    `loadPixiAsset` cuts a part out of it by the part's own url — so
 *    `world.ts` looks parts up exactly as before. A page that will not load
 *    falls back to the parts' own PNGs; a bundle with no pages loads the PNGs.
 *    A story still being made grows while it plays (`stream.ts`): each new
 *    revision's parts come on new pages, and `registerAssets` indexes only
 *    those, never re-registering a part that may already be on screen.
 * 4. **`MemoryBudget`.** A byte counter over *decoded* GPU bytes (`w × h × 4`),
 *    not file size. Before loading scene N+1 it (1) unloads what the current
 *    scene no longer needs, (2) defers the preload to the transition when even
 *    that does not fit — and it never exceeds the budget.
 *
 * pixi is imported **lazily**, inside the default `TexturePort`, so this
 * module (and `loader.test.ts`) runs with no renderer at all.
 */

import type { Bundle, BundleAssetKind } from '@/schema/story'

import type { DeviceProfile } from './device'
import { cuesOf } from './runner'
import { puppetTextureIds } from './puppet'
import { spriteAssetIds } from './sprite'

/* --------------------------------------------------------------- urls */

/**
 * Resolve one `assets[].url` against the bundle's own directory.
 * Relative (`tex/<sha>.png`), root-absolute (`/api/media/…`) and fully
 * qualified urls all work, and so does a `baseUrl` that is itself relative.
 */
export function resolveAssetUrl(url: string, baseUrl?: string | URL): string {
  if (!baseUrl) return url
  const base = typeof baseUrl === 'string' ? baseUrl : baseUrl.toString()
  try {
    const origin = typeof location !== 'undefined' ? location.href : 'http://localhost/'
    return new URL(url, new URL(base, origin)).toString()
  } catch {
    // No `location` (node) and a relative base: keep it textual but still correct.
    return base.endsWith('/') ? `${base}${url.replace(/^\.?\//, '')}` : `${base}/${url.replace(/^\.?\//, '')}`
  }
}

/** `baseUrl` default of §4.6.4: the directory that contains `bundle.json`. */
export function baseUrlOfBundle(bundleUrl: string): string {
  try {
    const origin = typeof location !== 'undefined' ? location.href : 'http://localhost/'
    return new URL('.', new URL(bundleUrl, origin)).toString()
  } catch {
    const i = bundleUrl.lastIndexOf('/')
    return i < 0 ? './' : bundleUrl.slice(0, i + 1)
  }
}

/* --------------------------------------------------------- texture port */

/** The renderer's side of loading. Swapped for a fake in tests. */
export interface TexturePort {
  load(id: string, url: string, kind?: BundleAssetKind): Promise<void>
  unload(id: string): Promise<void>
}

/**
 * The pixi parser to use for a bundle asset, chosen from `assets[].kind`
 * rather than from the URL.
 *
 * This is load-bearing against a real studio bundle: media is served from the
 * API (`/api/media/<uid>/original`), so an asset URL may carry **no extension
 * at all** or an extension followed by a query string. Pixi picks its parser
 * by sniffing the extension, and with either shape it gives up with "could not
 * be loaded as we don't know how to parse it" and the stage stays black.
 * `kind` is in the bundle for exactly this reason, so we name the parser and
 * never sniff.
 */
export function loadParserFor(kind: BundleAssetKind | undefined): string | undefined {
  switch (kind) {
    case 'plate':
    case 'poster':
    case 'texture':
    case 'pack':
    case 'object':
      return 'loadTextures'
    case 'font':
      return 'loadWebFont'
    // 'video' and 'audio' are not pixi textures: the plate video is a DOM
    // element (§8.1) and audio belongs to `audio.ts`.
    default:
      return undefined
  }
}

/**
 * Source options per kind. Puppet parts are drawn at ~0.3–0.5 scale and
 * rotate every frame; with `antialias:false` on the stage, mipmaps are what
 * keeps them from shimmering (research 02 §6.10).
 */
function loadDataFor(kind: BundleAssetKind | undefined): Record<string, unknown> | undefined {
  return kind === 'texture' || kind === 'pack' ? { autoGenerateMipmaps: true } : undefined
}

/* ------------------------------------------------------- packed parts */

/** Where one part sits on its pack page (absolute urls). */
interface PackedFrame {
  pageUrl: string
  x: number
  y: number
  w: number
  h: number
}

/**
 * Part url → its place on a page, for every packed part of every bundle a
 * loader was made for. Module-level on purpose: `world.ts` and `puppet.ts`
 * only ever know a part by its url, and `loadPixiAsset` is where they ask.
 */
const packedFrames = new Map<string, PackedFrame>()
/** Part url → the texture cut out of its page (one per part, shared by every view). */
const frameTextures = new Map<string, unknown>()

/** The page a part is packed on, when the bundle declares both (else `null`: load the part itself). */
export function packOf(bundle: Bundle, id: string): { id: string; x: number; y: number } | null {
  const a = bundle.assets[id] as (Bundle['assets'][string] & { pack?: { id: string; x: number; y: number } }) | undefined
  const ref = a?.kind === 'texture' ? a.pack : undefined
  if (!ref || typeof ref.id !== 'string') return null
  return bundle.assets[ref.id]?.kind === 'pack' ? ref : null
}

/** Stop cutting parts out of `pageUrl` (it failed or was unloaded): they load from their own urls. */
export function forgetPackPage(pageUrl: string): void {
  for (const [url, frame] of [...packedFrames]) {
    if (frame.pageUrl !== pageUrl) continue
    packedFrames.delete(url)
    const tex = frameTextures.get(url) as { destroy?: (all?: boolean) => void } | undefined
    frameTextures.delete(url)
    tex?.destroy?.(false)
  }
}

/** Test seam: the frame a part url resolves to, or undefined. */
export function packedFrameOf(url: string): Readonly<PackedFrame> | undefined {
  return packedFrames.get(url)
}

async function loadPackedFrame<T>(url: string, frame: PackedFrame): Promise<T> {
  const cached = frameTextures.get(url) as ({ destroyed?: boolean } & T) | undefined
  if (cached && !cached.destroyed) return cached
  const { Assets, Rectangle, Texture } = await import('pixi.js')
  let page: InstanceType<typeof Texture>
  try {
    page = (await loadPixiAsset(frame.pageUrl, 'pack')) as InstanceType<typeof Texture>
  } catch (err) {
    console.warn(`player/loader: pack page "${frame.pageUrl}" unavailable, parts load one by one`, err)
    forgetPackPage(frame.pageUrl)
    return (await Assets.load({ src: url, parser: 'loadTextures', data: { autoGenerateMipmaps: true } })) as T
  }
  const again = frameTextures.get(url) as ({ destroyed?: boolean } & T) | undefined
  if (again && !again.destroyed) return again
  const tex = new Texture({ source: page.source, frame: new Rectangle(frame.x, frame.y, frame.w, frame.h), label: url })
  frameTextures.set(url, tex)
  return tex as T
}

/**
 * Load (or take from the cache) one bundle asset, naming the parser from its
 * `kind`. Every pixi call site in the player goes through this (or the port
 * below), so a studio URL with no extension or a query string resolves the
 * same as `tex/<sha>.png` — and a packed part resolves to its cut of the page.
 */
export async function loadPixiAsset<T>(url: string, kind: BundleAssetKind): Promise<T> {
  const frame = kind === 'texture' ? packedFrames.get(url) : undefined
  if (frame) return loadPackedFrame<T>(url, frame)
  const { Assets } = await import('pixi.js')
  if (Assets.cache.has(url)) return Assets.get(url) as T
  const parser = loadParserFor(kind)
  const data = loadDataFor(kind)
  return (await (parser ? Assets.load({ src: url, parser, ...(data ? { data } : {}) }) : Assets.load(url))) as T
}

/** pixi `Assets`, imported only when a real renderer is in play. */
export function pixiTexturePort(): TexturePort {
  const urls = new Map<string, string>()
  const kinds = new Map<string, BundleAssetKind | undefined>()
  return {
    async load(id, url, kind) {
      urls.set(id, url)
      kinds.set(id, kind)
      if (kind === 'audio' || kind === 'video') return
      await loadPixiAsset(url, kind ?? 'plate')
    },
    async unload(id) {
      const url = urls.get(id)
      if (!url) return
      urls.delete(id)
      // The parts cut from a page go with it (the loader re-registers them if it loads it again).
      if (kinds.get(id) === 'pack') forgetPackPage(url)
      kinds.delete(id)
      const { Assets } = await import('pixi.js')
      await Assets.unload(url)
    },
  }
}

/** A port that resolves immediately — headless playback, vitest, and the audio-only fallback. */
export function nullTexturePort(): TexturePort {
  return { load: async () => {}, unload: async () => {} }
}

/* -------------------------------------------------------- memory budget */

/** One asset a scene would bring in, with what it costs decoded. */
export interface IncomingAsset {
  id: string
  bytes: number
}

export interface BudgetPlan {
  /** Ids to `Assets.unload` before loading anything new. */
  unload: string[]
  /** True when the incoming set does not fit even after evicting: the N+1 preload waits for the transition (§8.5). */
  defer: boolean
  /** Decoded bytes the plan ends up using. Never above `budget`. */
  projected: number
}

/**
 * A byte counter over uploaded textures. It knows nothing about pixi; the
 * loader tells it what it uploaded and asks it what to do next.
 */
export class MemoryBudget {
  private readonly bytes = new Map<string, number>()
  private _budget: number

  constructor(budgetBytes: number) {
    this._budget = Math.max(0, budgetBytes)
  }

  get budget(): number {
    return this._budget
  }

  setBudget(bytes: number): void {
    this._budget = Math.max(0, bytes)
  }

  get used(): number {
    let n = 0
    for (const v of this.bytes.values()) n += v
    return n
  }

  get free(): number {
    return Math.max(0, this._budget - this.used)
  }

  has(id: string): boolean {
    return this.bytes.has(id)
  }

  bytesOf(id: string): number {
    return this.bytes.get(id) ?? 0
  }

  ids(): string[] {
    return [...this.bytes.keys()]
  }

  /** Record an upload. Re-noting an id replaces its size. */
  note(id: string, bytes: number): void {
    this.bytes.set(id, Math.max(0, bytes))
  }

  release(id: string): void {
    this.bytes.delete(id)
  }

  clear(): void {
    this.bytes.clear()
  }

  /**
   * §8.5's steps, in order, as one pure decision.
   *
   * - `pinned`: ids the current scene needs — never unloaded.
   * - `evictable`: loaded ids the current scene does not need any more.
   * - `incoming`: what scene N+1 brings in (plate, puppet parts, props).
   */
  plan(input: { pinned: Iterable<string>; evictable: Iterable<string>; incoming: readonly IncomingAsset[] }): BudgetPlan {
    const pinned = new Set(input.pinned)
    const evictable = [...input.evictable].filter((id) => !pinned.has(id) && this.bytes.has(id))
    const incomingNew = input.incoming.filter((c) => !this.bytes.has(c.id))
    const sumIncoming = incomingNew.reduce((n, c) => n + Math.max(0, c.bytes), 0)

    const resident = (skip: Set<string>): number => {
      let n = 0
      for (const [id, b] of this.bytes) if (!skip.has(id)) n += b
      return n
    }

    const unload: string[] = []
    let projected = resident(new Set()) + sumIncoming
    if (projected <= this._budget) return { unload, defer: false, projected }

    // (1) unload what the current scene no longer needs — biggest first, so we stop early.
    const bySize = [...evictable].sort((a, b) => this.bytesOf(b) - this.bytesOf(a))
    for (const id of bySize) {
      unload.push(id)
      projected = resident(new Set(unload)) + sumIncoming
      if (projected <= this._budget) return { unload, defer: false, projected }
    }

    // (2) still too big: defer the preload to the transition.
    return { unload, defer: true, projected: resident(new Set(unload)) }
  }
}

/* --------------------------------------------------------- bundle loader */

export interface LoadProgress {
  /** Byte-weighted, 0..1. */
  ratio: number
  loadedBytes: number
  totalBytes: number
  /** The asset that just finished, for a debug overlay. */
  id: string | null
  sceneIndex: number
}

export interface BundleLoaderOptions {
  bundle: Bundle
  baseUrl: string
  device: DeviceProfile
  textures?: TexturePort | undefined
  onProgress?: ((p: LoadProgress) => void) | undefined
}

/**
 * Decoded bytes an asset costs on the GPU: `w × h × 4` (a plate with no size counts as 1920×1080).
 * A packed part costs nothing of its own: its page carries the bytes.
 */
export function decodedBytesOf(bundle: Bundle, id: string): number {
  const a = bundle.assets[id]
  if (!a) return 0
  if (a.kind === 'audio' || a.kind === 'font') return 0
  if (packOf(bundle, id)) return 0
  const w = a.w ?? (a.kind === 'texture' ? 256 : 1920)
  const h = a.h ?? (a.kind === 'texture' ? 256 : 1080)
  return w * h * 4
}

/** How many assets load at once. Six is the browser's own per-host cap on HTTP/1.1. */
export const LOAD_CONCURRENCY = 6

/**
 * Progress weight of an asset whose `bytes` is 0 (the API derives it, so the baker cannot know):
 * roughly what one weighs on the wire, so the bar does not sit still while the plate comes in.
 */
const WEIGHT_BY_KIND: Partial<Record<BundleAssetKind, number>> = {
  plate: 120_000,
  poster: 150_000,
  pack: 250_000,
  texture: 20_000,
  object: 40_000,
}

function weightOf(bundle: Bundle, id: string): number {
  const a = bundle.assets[id]
  const bytes = a?.bytes ?? 0
  if (bytes > 0) return bytes
  return (a ? WEIGHT_BY_KIND[a.kind] : undefined) ?? 1
}

/** Run `task` over `items`, at most `limit` at a time, in order of start. */
export async function runPool<T>(items: readonly T[], limit: number, task: (item: T) => Promise<void>): Promise<void> {
  let next = 0
  const worker = async (): Promise<void> => {
    while (next < items.length) {
      const item = items[next++] as T
      await task(item)
    }
  }
  await Promise.all(Array.from({ length: Math.max(1, Math.min(limit, items.length)) }, worker))
}

/** Every part PNG the cast (and puppet props) can draw — all views, since an action may switch view. */
export function puppetAssetIds(bundle: Bundle): string[] {
  const out = new Set<string>()
  for (const entry of Object.values(bundle.cast)) {
    for (const id of puppetTextureIds(entry?.puppet)) out.add(id)
    for (const id of spriteAssetIds(entry?.sprite)) out.add(id)
  }
  for (const entry of Object.values(bundle.props)) for (const id of puppetTextureIds(entry?.puppet)) out.add(id)
  return [...out]
}

export class BundleLoader {
  readonly bundle: Bundle
  readonly baseUrl: string
  readonly budget: MemoryBudget

  private device: DeviceProfile
  private readonly textures: TexturePort
  private readonly onProgress: ((p: LoadProgress) => void) | null
  private readonly loaded = new Set<string>()
  private readonly inflight = new Map<string, Promise<void>>()
  /** Asset ids each scene index pulled in, for `unloadScene`. */
  private readonly bySceneId = new Map<number, Set<string>>()
  private readonly warned = new Set<string>()
  /** Pack page id → the part ids packed on it (they are resident exactly while it is). */
  private readonly packMembers = new Map<string, string[]>()
  /** The frames this loader registered for `loadPixiAsset`, removed again on `destroy`. */
  private readonly frames = new Map<string, PackedFrame>()
  /** Pages that failed to load: their parts load one by one, including parts registered later. */
  private readonly deadPages = new Set<string>()

  constructor(opts: BundleLoaderOptions) {
    this.bundle = opts.bundle
    this.baseUrl = opts.baseUrl
    this.device = opts.device
    this.textures = opts.textures ?? nullTexturePort()
    this.onProgress = opts.onProgress ?? null
    this.budget = new MemoryBudget(opts.device.textureBudgetBytes)
    this.registerAssets(Object.keys(this.bundle.assets))
  }

  /**
   * Index packed parts: by page for the loader, by url for `loadPixiAsset`. The constructor passes
   * every asset id; a story that is still being made (`stream.ts`) passes the ids each new revision
   * added to the bundle, which already holds them. An id already registered is left alone — its
   * frame may be on screen — and so is a part on a page that failed to load.
   */
  registerAssets(ids: readonly string[]): void {
    for (const id of ids) {
      const a = this.bundle.assets[id]
      const ref = packOf(this.bundle, id)
      if (!ref || !a?.w || !a.h || this.deadPages.has(ref.id)) continue
      const members = this.packMembers.get(ref.id) ?? []
      if (members.includes(id)) continue
      const partUrl = resolveAssetUrl(a.url, this.baseUrl)
      members.push(id)
      this.packMembers.set(ref.id, members)
      const pageUrl = resolveAssetUrl(this.bundle.assets[ref.id]!.url, this.baseUrl)
      const frame: PackedFrame = { pageUrl, x: ref.x, y: ref.y, w: a.w, h: a.h }
      this.frames.set(partUrl, frame)
      packedFrames.set(partUrl, frame)
      // A part added to a page that is already up is resident with it.
      if (this.loaded.has(ref.id)) this.loaded.add(id)
    }
  }

  /** The page part `id` loads with, or `null` when it loads on its own. */
  private pageOf(id: string): string | null {
    const ref = packOf(this.bundle, id)
    return ref && this.packMembers.has(ref.id) && this.frames.has(this.url(id) ?? '') ? ref.id : null
  }

  setDevice(device: DeviceProfile): void {
    this.device = device
    this.budget.setBudget(device.textureBudgetBytes)
  }

  url(id: string): string | null {
    const a = this.bundle.assets[id]
    if (!a) {
      if (!this.warned.has(id)) {
        this.warned.add(id)
        console.warn(`player/loader: bundle has no asset "${id}"`)
      }
      return null
    }
    return resolveAssetUrl(a.url, this.baseUrl)
  }

  isLoaded(id: string): boolean {
    return this.loaded.has(id)
  }

  /** Every asset id scene `i` can touch: plate, audio, props, and every puppet's part PNGs. */
  sceneAssetIds(i: number): string[] {
    const scene = this.bundle.scenes[i]
    if (!scene) return []
    const ids = new Set<string>(scene.preload)
    ids.add(scene.plate.asset)
    if (scene.plate.video) ids.add(scene.plate.video)
    if (scene.plate.poster) ids.add(scene.plate.poster)
    for (const occ of scene.plate.occluders) if (occ.asset) ids.add(occ.asset)
    if (scene.music) ids.add(scene.music)
    if (scene.ambient) ids.add(scene.ambient)
    for (const cue of cuesOf(scene)) {
      if (cue.op === 'sfx') ids.add(cue.sound)
      if (cue.op === 'music' && cue.track) ids.add(cue.track)
      const audio = (cue as { audio?: { url?: string } | null }).audio
      if (audio?.url) ids.add(audio.url)
    }
    for (const p of Object.values(this.bundle.props)) if (p?.image) ids.add(p.image)
    for (const id of puppetAssetIds(this.bundle)) ids.add(id)
    // A packed part needs its page: the page is what gets loaded, kept and unloaded.
    for (const id of [...ids]) {
      const page = this.pageOf(id)
      if (page) ids.add(page)
    }
    // Only ids the bundle actually declares are loadable.
    return [...ids].filter((id) => this.bundle.assets[id] !== undefined)
  }

  /**
   * Byte-weighted, parallel load of a set of asset ids (`LOAD_CONCURRENCY` at
   * a time, heaviest first so the plate and the pack page start at once).
   * Packed parts ride on their page: one request for all of them. Progress
   * fires per finished request with the running ratio; a `bytes: 0` entry
   * still moves the bar by its kind's weight.
   */
  async loadIds(ids: readonly string[], sceneIndex: number): Promise<void> {
    const track = this.bySceneId.get(sceneIndex) ?? new Set<string>()
    this.bySceneId.set(sceneIndex, track)

    const units: string[] = []
    const seen = new Set<string>()
    for (const id of ids) {
      track.add(id)
      const unit = this.pageOf(id) ?? id
      track.add(unit)
      if (!seen.has(unit)) {
        seen.add(unit)
        units.push(unit)
      }
    }
    const weights = new Map<string, number>()
    let total = 0
    for (const id of units) {
      const w = Math.max(1, weightOf(this.bundle, id))
      weights.set(id, w)
      total += w
    }
    units.sort((a, b) => (weights.get(b) ?? 0) - (weights.get(a) ?? 0))

    let done = 0
    this.onProgress?.({ ratio: total === 0 ? 1 : 0, loadedBytes: 0, totalBytes: total, id: null, sceneIndex })
    await runPool(units, LOAD_CONCURRENCY, async (id) => {
      try {
        await this.loadOne(id)
      } catch (err) {
        // A missing asset must never stop the story: the scene plays without it.
        console.warn(`player/loader: could not load "${id}"`, err)
        // A page that will not load: its parts come one by one from their own urls.
        const members = this.packMembers.has(id) ? this.dropPage(id).filter((m) => ids.includes(m)) : []
        if (members.length > 0) await this.loadLoose(members)
      }
      done += weights.get(id) ?? 1
      this.onProgress?.({
        ratio: total === 0 ? 1 : Math.min(1, done / total),
        loadedBytes: done,
        totalBytes: total,
        id,
        sceneIndex,
      })
    })
  }

  /** Parts whose page failed, each from its own url (nothing else to fall back to: warn and go on). */
  private async loadLoose(ids: readonly string[]): Promise<void> {
    await runPool(ids, LOAD_CONCURRENCY, async (id) => {
      try {
        await this.loadOne(id)
      } catch (err) {
        console.warn(`player/loader: could not load "${id}"`, err)
      }
    })
  }

  /** Stop using page `pageId` (it failed): its parts load on their own from now on. Returns them. */
  private dropPage(pageId: string): string[] {
    const members = this.packMembers.get(pageId) ?? []
    this.packMembers.delete(pageId)
    this.deadPages.add(pageId)
    for (const id of members) {
      const url = this.url(id)
      if (url) this.frames.delete(url)
    }
    const pageUrl = this.url(pageId)
    if (pageUrl) forgetPackPage(pageUrl)
    return members
  }

  private loadOne(id: string): Promise<void> {
    if (this.loaded.has(id)) return Promise.resolve()
    const existing = this.inflight.get(id)
    if (existing) return existing
    const page = this.pageOf(id)
    if (page) return this.loadOne(page)
    const url = this.url(id)
    if (!url) return Promise.resolve()
    const kind = this.bundle.assets[id]?.kind
    const p = this.textures
      .load(id, url, kind)
      .then(() => {
        this.loaded.add(id)
        const bytes = decodedBytesOf(this.bundle, id)
        if (bytes > 0) this.budget.note(id, bytes)
        if (kind === 'pack') {
          // Its parts are resident now: re-register them (an unload may have forgotten them).
          for (const m of this.packMembers.get(id) ?? []) {
            this.loaded.add(m)
            const mu = this.url(m)
            const frame = mu ? this.frames.get(mu) : undefined
            if (mu && frame) packedFrames.set(mu, frame)
          }
        }
      })
      .finally(() => {
        this.inflight.delete(id)
      })
    this.inflight.set(id, p)
    return p
  }

  /** Scene 0 (and every hard scene switch): everything, before play may start. */
  async preloadScene(i: number): Promise<void> {
    await this.loadIds(this.sceneAssetIds(i), i)
  }

  /**
   * Scene N+1 in the background, inside the budget (§8.5). Returns the plan
   * so the caller can show the short spinner when it had to be deferred.
   */
  async preloadNext(currentScene: number, _currentT = 0): Promise<BudgetPlan> {
    const next = currentScene + 1
    if (next >= this.bundle.scenes.length) {
      return { unload: [], defer: false, projected: this.budget.used }
    }
    const pinned = this.sceneAssetIds(currentScene)
    const incomingIds = this.sceneAssetIds(next).filter((id) => !this.loaded.has(id))
    const plan = this.budget.plan({
      pinned,
      evictable: this.evictableNow(currentScene),
      incoming: incomingIds.map((id) => ({ id, bytes: decodedBytesOf(this.bundle, id) })),
    })
    for (const id of plan.unload) await this.unloadId(id)
    if (plan.defer) {
      console.warn('player/loader: scene %d preload deferred to the transition (budget)', next)
      return plan
    }
    await this.loadIds(incomingIds, next)
    return plan
  }

  /** Ids safe to drop: loaded, and not needed by scene `sceneIndex`. */
  evictableNow(sceneIndex: number): string[] {
    const keep = new Set(this.sceneAssetIds(sceneIndex))
    return [...this.loaded].filter((id) => !keep.has(id))
  }

  private async unloadId(id: string): Promise<void> {
    if (!this.loaded.has(id)) return
    this.loaded.delete(id)
    this.budget.release(id)
    // A packed part has no texture of its own to drop: its page holds it.
    if (this.pageOf(id)) return
    // A page takes its parts with it.
    for (const m of this.packMembers.get(id) ?? []) this.loaded.delete(m)
    try {
      await this.textures.unload(id)
    } catch (err) {
      console.warn(`player/loader: unload "${id}" failed`, err)
    }
  }

  /**
   * Scene N−1 goes away once N is running (§8.5). Ids shared with a live scene stay: everything a
   * kept scene NEEDS (`sceneAssetIds`), not only what it pulled in — a longer story plays every
   * part on one plate with one cast, so part 2 pulls in nothing new and part 3 starting must not
   * drop the plate and every puppet with part 1.
   */
  async unloadScene(i: number, keepScenes: readonly number[] = []): Promise<void> {
    const ids = this.bySceneId.get(i)
    if (!ids) return
    const keep = new Set<string>()
    for (const k of keepScenes) {
      for (const id of this.bySceneId.get(k) ?? []) keep.add(id)
      for (const id of this.sceneAssetIds(k)) keep.add(id)
    }
    for (const id of ids) if (!keep.has(id)) await this.unloadId(id)
    this.bySceneId.delete(i)
  }

  async destroy(): Promise<void> {
    for (const id of [...this.loaded]) await this.unloadId(id)
    this.bySceneId.clear()
    this.budget.clear()
    for (const [url, frame] of this.frames) if (packedFrames.get(url) === frame) packedFrames.delete(url)
  }
}
